"use client";

import { useState } from "react";
import { Twitter, Facebook, Linkedin, Link2, Check, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  /** 共有対象の絶対 or 相対パス（相対なら location.origin を補完） */
  url: string;
  title: string;
  className?: string;
};

export default function ShareButtons({ url, title, className }: Props) {
  const [copied, setCopied] = useState(false);

  const resolveUrl = () => {
    if (/^https?:\/\//.test(url)) return url;
    if (typeof window !== "undefined") return `${window.location.origin}${url}`;
    return url;
  };

  const share = (kind: "x" | "facebook" | "line" | "linkedin") => {
    const u = encodeURIComponent(resolveUrl());
    const t = encodeURIComponent(title);
    const map: Record<typeof kind, string> = {
      x: `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      line: `https://social-plugins.line.me/lineit/share?url=${u}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    };
    window.open(map[kind], "_blank", "noopener,noreferrer,width=600,height=600");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(resolveUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* クリップボード非対応環境は無視 */
    }
  };

  const base =
    "inline-flex h-10 w-10 items-center justify-center rounded-full border border-line text-navy-700 transition-all hover:border-navy-700 hover:bg-navy-700 hover:text-white";

  return (
    <div className={cn("flex flex-wrap items-center gap-2.5", className)}>
      <span className="mr-1 font-en text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
        Share
      </span>
      <button type="button" onClick={() => share("x")} className={base} aria-label="Xでシェア">
        <Twitter className="h-[18px] w-[18px]" aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => share("facebook")}
        className={base}
        aria-label="Facebookでシェア"
      >
        <Facebook className="h-[18px] w-[18px]" aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => share("line")}
        className={base}
        aria-label="LINEでシェア"
      >
        <MessageCircle className="h-[18px] w-[18px]" aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => share("linkedin")}
        className={base}
        aria-label="LinkedInでシェア"
      >
        <Linkedin className="h-[18px] w-[18px]" aria-hidden />
      </button>
      <button
        type="button"
        onClick={copy}
        className={cn(base, copied && "border-navy-700 bg-navy-700 text-white")}
        aria-label="リンクをコピー"
      >
        {copied ? <Check className="h-[18px] w-[18px]" aria-hidden /> : <Link2 className="h-[18px] w-[18px]" aria-hidden />}
      </button>
    </div>
  );
}
